/**
 * Collectible - Collectible entity for batteries, health pickups, lore items, and upgrades
 * Represents items the player can pick up in the game
 */

import { Entity } from './Entity';
import type { Vector2 } from '../../types/game';
import { getPhysicsEngine } from '../../engine/PhysicsEngine';
import { getCollisionSystem } from '../../engine/CollisionSystem';
import type { Player } from './Player';
import { playCollectiblePickup } from '../audio/SoundEffects';

export type CollectibleType = 'lore' | 'battery' | 'health' | 'upgrade' | 'weapon' | 'taser' | 'key'
  | 'keycard' | 'vhs_tape' | 'pizza' | 'fuse' | 'badge';

export class Collectible extends Entity {
  // Collectible properties
  public collectibleId: string;
  public collectibleType: CollectibleType;
  public value: number;
  public autoCollect: boolean;
  public glowColor?: string;
  public isCollected: boolean = false;

  // Pickup settings
  private pickupRadius: number = 40; // pixels - how close player needs to be
  private floatAmplitude: number = 5; // pixels - how much it bobs up/down
  private floatSpeed: number = 2; // speed of floating animation
  private floatTimer: number = 0;
  private baseY: number; // Original Y position for floating animation

  constructor(
    id: string,
    collectibleType: CollectibleType,
    position: Vector2,
    size: { width: number; height: number },
    value: number = 1,
    autoCollect: boolean = true
  ) {
    super(id, 'collectible', position, size);

    this.collectibleId = id;
    this.collectibleType = collectibleType;
    this.value = value;
    this.autoCollect = autoCollect;
    this.baseY = position.y;

    // Set glow color based on type
    this.setGlowColor();

    // Collectibles don't have health
    this.health = 1;
    this.maxHealth = 1;
  }

  /**
   * Set glow color based on collectible type
   */
  private setGlowColor(): void {
    switch (this.collectibleType) {
      case 'battery':
        this.glowColor = '#00ffff'; // Cyan for batteries
        break;
      case 'health':
        this.glowColor = '#ff0000'; // Red for health
        break;
      case 'lore':
        this.glowColor = '#ffff00'; // Yellow for lore items
        break;
      case 'upgrade':
        this.glowColor = '#ff00ff'; // Magenta for upgrades
        break;
      case 'weapon':
        this.glowColor = '#ff8800'; // Orange for weapons
        break;
      case 'keycard':
        this.glowColor = '#00aaff'; // Blue
        break;
      case 'vhs_tape':
        this.glowColor = '#cc44ff'; // Purple
        break;
      case 'pizza':
        this.glowColor = '#ff6600'; // Orange
        break;
      case 'fuse':
        this.glowColor = '#ffcc00'; // Amber
        break;
      case 'badge':
        this.glowColor = '#00ff88'; // Mint green
        break;
      case 'taser':
        this.glowColor = '#ffee00'; // Electric yellow for taser
        break;
      case 'key':
        this.glowColor = '#ffd700'; // Gold for keys
        break;
    }
  }

  /**
   * Register collectible with physics engine (no gravity, static)
   */
  protected override registerPhysics(): void {
    const physicsEngine = getPhysicsEngine();
    physicsEngine.addEntity(this as any, {
      mass: 0.1,
      gravity: 0, // No gravity for collectibles (they float)
      friction: 0,
      restitution: 0,
      isKinematic: true, // Not affected by physics
      isGrounded: false,
      canJump: false,
    });
  }

  /**
   * Register collectible with collision system (as trigger)
   */
  protected override registerCollision(): void {
    const collisionSystem = getCollisionSystem();
    collisionSystem.addCollider(this as any, {
      type: 'box',
      isTrigger: true, // Collectibles are triggers, don't block movement
      layer: 'trigger',
    });
  }

  /**
   * Update collectible (floating animation, pickup detection)
   */
  public update(deltaTime: number): void {
    if (this.destroyed || !this.active || this.isCollected) return;

    // Floating animation
    this.updateFloatingAnimation(deltaTime);
  }

  /**
   * Render collectible (override with Three.js sprite)
   */
  public render(): void {
    // To be implemented with Three.js sprite rendering
  }

  /**
   * Update floating animation
   */
  private updateFloatingAnimation(deltaTime: number): void {
    this.floatTimer += deltaTime * this.floatSpeed;

    // Sine wave for smooth up/down movement
    const offset = Math.sin(this.floatTimer) * this.floatAmplitude;
    this.position.y = this.baseY + offset;
  }

  /**
   * Check if player is in pickup range
   */
  public canPickup(player: Player): boolean {
    if (this.isCollected || this.destroyed || !this.active) {
      return false;
    }

    const distance = this.distanceTo(player);
    return distance <= this.pickupRadius;
  }

  /**
   * Collect this item
   */
  public collect(player: Player): boolean {
    if (!this.canPickup(player)) {
      return false;
    }

    // Apply collectible effect based on type
    switch (this.collectibleType) {
      case 'battery':
        player.refillFlashlight(this.value);
        break;

      case 'health':
        player.heal(this.value);
        break;

      case 'lore':
        // Lore items don't have immediate effect, just collected
        // Will be handled by inventory/lore system later
        break;

      case 'upgrade':
        // Upgrades handled by progression system
        // For now, just mark as collected
        break;
    }

    // Mark as collected and destroy
    this.isCollected = true;
    this.onCollected();

    return true;
  }

  /**
   * Called when collectible is collected
   */
  protected onCollected(): void {
    // Play pickup sound effect
    playCollectiblePickup();

    // Trigger pickup animation/particles here (when particle system is implemented)

    // Destroy the collectible after a short delay to allow animations
    setTimeout(() => {
      this.destroy();
    }, 100);
  }

  /**
   * Get collectible info for UI/debugging
   */
  public getInfo(): {
    id: string;
    type: CollectibleType;
    value: number;
    glowColor?: string;
  } {
    return {
      id: this.collectibleId,
      type: this.collectibleType,
      value: this.value,
      glowColor: this.glowColor,
    };
  }

  // Getters
  public getCollectibleType(): CollectibleType {
    return this.collectibleType;
  }

  public getValue(): number {
    return this.value;
  }

  public isAutoCollect(): boolean {
    return this.autoCollect;
  }

  public getGlowColor(): string | undefined {
    return this.glowColor;
  }

  public isItemCollected(): boolean {
    return this.isCollected;
  }
}
